// assets/mbq-life-changing.js
/* Before/after handle and study tabs for sections/mbq-life-changing.liquid (ES module, no imports). */

/** Keeps a percent value inside the 4–96 range so the handle never sits flush with an edge. */
export function clampPercent(x) {
  return Math.max(4, Math.min(96, Number(x) || 0));
}

/** Turns a keydown key into the next clamped percent for the given current value, or null if the key is not handled. */
export function keyStep(key, current) {
  if (key === 'ArrowRight' || key === 'ArrowUp') return clampPercent(current + 5);
  if (key === 'ArrowLeft' || key === 'ArrowDown') return clampPercent(current - 5);
  if (key === 'Home') return 4;
  if (key === 'End') return 96;
  return null;
}

/** Wires pointer drag and keyboard control onto one before/after slider element. */
function initSlider(ba) {
  if (ba.dataset.mbqBaReady === 'true') return;
  ba.dataset.mbqBaReady = 'true';
  const set = (x) => {
    const v = Math.round(clampPercent(x));
    ba.style.setProperty('--mbq-x', `${v}%`);
    ba.setAttribute('aria-valuenow', String(v));
  };
  const fromEvent = (e) => {
    const r = ba.getBoundingClientRect();
    return ((e.clientX - r.left) / r.width) * 100;
  };
  let dragging = false;
  let moved = false;
  ba.addEventListener('pointerdown', (e) => {
    dragging = true;
    moved = false;
    ba.classList.add('is-dragging');
    if (typeof ba.setPointerCapture === 'function' && e.pointerId !== undefined) { try { ba.setPointerCapture(e.pointerId); } catch { /* jsdom */ } }
  });
  ba.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    moved = true;
    set(fromEvent(e));
  });
  ba.addEventListener('pointerup', (e) => {
    if (dragging && !moved) set(fromEvent(e));
    dragging = false;
    moved = false;
    ba.classList.remove('is-dragging');
  });
  ba.addEventListener('pointercancel', () => { dragging = false; moved = false; ba.classList.remove('is-dragging'); });
  ba.addEventListener('keydown', (e) => {
    const next = keyStep(e.key, Number(ba.getAttribute('aria-valuenow')) || 50);
    if (next === null) return;
    e.preventDefault();
    set(next);
  });
}

/** Wires click and arrow-key roving-tabindex navigation onto one study-tabs list. */
function initTabs(list) {
  if (list.dataset.mbqTabsReady === 'true') return;
  list.dataset.mbqTabsReady = 'true';
  const root = list.closest('section') || document;
  const tabs = [...list.querySelectorAll('[data-mbq-tab]')];
  const select = (tab, focus = false) => {
    tabs.forEach((t) => {
      const on = t === tab;
      t.setAttribute('aria-selected', String(on));
      t.tabIndex = on ? 0 : -1;
      const panel = root.querySelector(`[data-mbq-panel="${t.dataset.mbqTab}"]`);
      if (panel) panel.hidden = !on;
    });
    if (focus) tab.focus();
  };
  list.addEventListener('click', (e) => { const tab = e.target.closest('[data-mbq-tab]'); if (tab) select(tab); });
  list.addEventListener('keydown', (e) => {
    const delta = { ArrowRight: 1, ArrowLeft: -1 }[e.key];
    if (!delta) return;
    const current = tabs.indexOf(e.target.closest('[data-mbq-tab]'));
    if (current < 0) return;
    e.preventDefault();
    select(tabs[(current + delta + tabs.length) % tabs.length], true);
  });
}

/** Wires the Perso / Pro segmented control: it flips data-mbq-aud on the section, and CSS hides the other audience. */
function initAudience(group) {
  if (group.dataset.mbqAudReady === 'true') return;
  group.dataset.mbqAudReady = 'true';
  const section = group.closest('[data-mbq-life]');
  const buttons = [...group.querySelectorAll('[data-mbq-aud-btn]')];
  group.addEventListener('click', (e) => {
    const button = e.target.closest('[data-mbq-aud-btn]');
    if (!button || !section) return;
    section.setAttribute('data-mbq-aud', button.dataset.mbqAudBtn);
    buttons.forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
  });
}

/** Binds every before/after slider, study-tabs list and Perso/Pro control found under root (defaults to the document). */
export function initLife(root = document) {
  root.querySelectorAll('[data-mbq-ba]').forEach(initSlider);
  root.querySelectorAll('[data-mbq-tabs]').forEach(initTabs);
  root.querySelectorAll('[data-mbq-aud-group]').forEach(initAudience);
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initLife());
  else initLife();
  document.addEventListener('shopify:section:load', (event) => initLife(event.target));
}
